import {
  adminNotificationControlSchema,
  adminNotificationDeliveryListItemSchema,
  adminNotificationDeliverySchema,
  adminNotificationMetricsSchema,
  adminNotificationMutationResultSchema,
  type AdminNotificationControl,
  type AdminNotificationDelivery,
  type AdminNotificationMetrics,
  type AdminNotificationMutationResult,
} from '@steado/admin-contract/notifications';
import { paginationMetaSchema } from '@steado/admin-contract/pagination';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';

import type { ApiClient } from '@/lib/api/client';

import type { NotificationWindow, NotificationsSearch } from './notifications-search';

export const NOTIFICATION_METRICS_REFRESH_MS = 30_000;
export const DELIVERIES_PAGE_SIZE = 25;

/** The metrics endpoint caps its window at seven days. */
export const WINDOW_MINUTES: Readonly<Record<NotificationWindow, number>> = {
  '24h': 1_440,
  '7d': 10_080,
};

export function notificationMetricsQueryOptions(api: ApiClient, window: NotificationWindow = '24h') {
  return queryOptions({
    queryKey: ['admin', 'notification-metrics', window],
    queryFn: (): Promise<AdminNotificationMetrics> =>
      api.request({
        path: '/admin/notifications/metrics',
        query: { windowMinutes: WINDOW_MINUTES[window] },
        schema: adminNotificationMetricsSchema,
      }),
    refetchInterval: NOTIFICATION_METRICS_REFRESH_MS,
  });
}

const deliveriesPageSchema = z.object({
  items: z.array(adminNotificationDeliveryListItemSchema),
  pagination: paginationMetaSchema,
});
export type DeliveriesPage = z.infer<typeof deliveriesPageSchema>;

export function notificationDeliveriesQueryOptions(api: ApiClient, search: NotificationsSearch) {
  const query = {
    when: search.when ?? 'past',
    search: search.search,
    status: search.status,
    category: search.category,
    eventType: search.eventType,
    cursor: search.cursor,
    limit: DELIVERIES_PAGE_SIZE,
  };
  return queryOptions({
    queryKey: ['admin', 'notification-deliveries', query],
    queryFn: async (): Promise<DeliveriesPage> => {
      const response = await api.requestWithMeta({
        path: '/admin/notifications/deliveries',
        query,
        schema: z.array(adminNotificationDeliveryListItemSchema),
      });
      return deliveriesPageSchema.parse({ items: response.data, pagination: response.meta.pagination });
    },
    placeholderData: (previous) => previous,
    refetchInterval: NOTIFICATION_METRICS_REFRESH_MS,
  });
}

export function notificationControlsQueryOptions(api: ApiClient) {
  return queryOptions({
    queryKey: ['admin', 'notification-controls'],
    queryFn: (): Promise<AdminNotificationControl[]> =>
      api.request({ path: '/admin/notifications/controls', schema: z.array(adminNotificationControlSchema) }),
  });
}

export function notificationDeliveryQueryOptions(api: ApiClient, deliveryId: string | null) {
  return queryOptions({
    queryKey: ['admin', 'notification-delivery', deliveryId],
    queryFn: (): Promise<AdminNotificationDelivery> =>
      api.request({
        path: `/admin/notifications/deliveries/${deliveryId ?? ''}`,
        schema: adminNotificationDeliverySchema,
      }),
    enabled: deliveryId !== null,
  });
}

export function mutateNotificationDelivery(
  api: ApiClient,
  deliveryId: string,
  action: 'retry' | 'suppress',
): Promise<AdminNotificationMutationResult> {
  return api.request({
    path: `/admin/notifications/deliveries/${deliveryId}/${action}`,
    method: 'POST',
    body: {
      confirmation: deliveryId,
      reason: action === 'retry' ? 'INCIDENT_RESPONSE' : 'ROLLOUT_STOP',
    },
    schema: adminNotificationMutationResultSchema,
  });
}

export function setNotificationControl(
  api: ApiClient,
  control: AdminNotificationControl,
  enabled: boolean,
): Promise<AdminNotificationControl> {
  return api.request({
    path: `/admin/notifications/controls/${encodeURIComponent(control.key)}`,
    method: 'PUT',
    body: {
      enabled,
      confirmation: control.key,
      reason: enabled ? 'CONFIGURATION_CORRECTED' : 'ROLLOUT_STOP',
    },
    schema: adminNotificationControlSchema,
  });
}
